import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Award,
  GraduationCap,
  MoreHorizontal,
  Presentation,
  School,
  ShieldCheck,
  Target,
  UserSearch,
  type LucideIcon,
} from 'lucide-react';
import {
  AREAS_BY_USE_CASE,
  GRADE_BANDS,
  GRADE_BAND_USE_CASES,
  MAX_ASSESS_AREAS,
  ORG_USE_CASES,
  PRIMARY_GOALS,
  ROLES_BY_USE_CASE,
  type AssessArea,
  type GradeBand,
  type OnboardingRole,
  type OrgUseCase,
  type PrimaryGoal,
} from 'modules/auth/onboarding';
import { OptionCard, OptionChip } from './OptionCard';

// Answer pickers shared by the onboarding wizard and the org settings card.

const USE_CASE_ICONS: Record<OrgUseCase, LucideIcon> = {
  hiring: UserSearch,
  employee_training: Presentation,
  compliance: ShieldCheck,
  school: GraduationCap,
  training_center: School,
  exam_prep: Target,
  certification: Award,
  other: MoreHorizontal,
};

interface UseCasePickerProps {
  value: OrgUseCase | null;
  onChange: (value: OrgUseCase) => void;
}

export function UseCasePicker({ value, onChange }: UseCasePickerProps) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-3 sm:grid-cols-2" role="group" aria-label={t('auth.ob_usecase_title')}>
      {ORG_USE_CASES.map((key) => (
        <OptionCard
          key={key}
          icon={USE_CASE_ICONS[key]}
          label={t(`auth.ob_usecase_${key}`)}
          description={t(`auth.ob_usecase_${key}_desc`)}
          selected={value === key}
          onSelect={() => onChange(key)}
        />
      ))}
    </div>
  );
}

interface RolePickerProps {
  useCase: OrgUseCase;
  value: OnboardingRole | null;
  onChange: (value: OnboardingRole) => void;
}

export function RolePicker({ useCase, value, onChange }: RolePickerProps) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-2.5 sm:grid-cols-2" role="group" aria-label={t('auth.ob_role_title')}>
      {ROLES_BY_USE_CASE[useCase].map((key) => (
        <OptionCard key={key} label={t(`auth.ob_jobrole_${key}`)} selected={value === key} onSelect={() => onChange(key)} />
      ))}
    </div>
  );
}

interface FocusPickerProps {
  useCase: OrgUseCase;
  areas: AssessArea[];
  onAreasChange: (areas: AssessArea[]) => void;
  gradeBand: GradeBand | null;
  onGradeBandChange: (value: GradeBand | null) => void;
  goal: PrimaryGoal | null;
  onGoalChange: (value: PrimaryGoal | null) => void;
}

/** Assessed areas (up to MAX_ASSESS_AREAS), grade band for education use cases, and the first goal. All optional. */
export function FocusPicker({
  useCase,
  areas,
  onAreasChange,
  gradeBand,
  onGradeBandChange,
  goal,
  onGoalChange,
}: FocusPickerProps) {
  const { t } = useTranslation();
  const atLimit = areas.length >= MAX_ASSESS_AREAS;
  const toggleArea = (area: AssessArea) =>
    onAreasChange(areas.includes(area) ? areas.filter((a) => a !== area) : [...areas, area]);

  return (
    <div className="space-y-6">
      <fieldset className="space-y-3">
        <legend className="sr-only">{t('auth.ob_focus_title')}</legend>
        <div className="flex flex-wrap gap-2">
          {AREAS_BY_USE_CASE[useCase].map((area) => (
            <OptionChip
              key={area}
              label={t(`auth.ob_area_${area}`)}
              selected={areas.includes(area)}
              disabled={atLimit}
              onToggle={() => toggleArea(area)}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {t('auth.ob_focus_count', { count: areas.length, max: MAX_ASSESS_AREAS })}
        </p>
      </fieldset>

      {GRADE_BAND_USE_CASES.includes(useCase) ? (
        <fieldset className="space-y-3">
          <legend className="text-sm font-medium text-foreground/90">
            {t('auth.ob_gradeband_label')} <span className="font-normal text-muted-foreground">· {t('auth.ob_optional')}</span>
          </legend>
          <div className="flex flex-wrap gap-2">
            {GRADE_BANDS.map((band) => (
              <OptionChip
                key={band}
                label={t(`auth.ob_gradeband_${band}`)}
                selected={gradeBand === band}
                onToggle={() => onGradeBandChange(gradeBand === band ? null : band)}
              />
            ))}
          </div>
        </fieldset>
      ) : null}

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-foreground/90">
          {t('auth.ob_goal_label')} <span className="font-normal text-muted-foreground">· {t('auth.ob_optional')}</span>
        </legend>
        <div className="flex flex-wrap gap-2">
          {PRIMARY_GOALS.map((key) => (
            <OptionChip
              key={key}
              label={t(`auth.ob_goal_${key}`)}
              selected={goal === key}
              onToggle={() => onGoalChange(goal === key ? null : key)}
            />
          ))}
        </div>
      </fieldset>
    </div>
  );
}

/** Keeps only answers the new use case still offers (switching use case must not submit hidden picks). */
export function pruneForUseCase(
  useCase: OrgUseCase,
  answers: { role: OnboardingRole | null; areas: AssessArea[]; gradeBand: GradeBand | null },
): { role: OnboardingRole | null; areas: AssessArea[]; gradeBand: GradeBand | null } {
  return {
    role: answers.role && ROLES_BY_USE_CASE[useCase].includes(answers.role) ? answers.role : null,
    areas: answers.areas.filter((a) => AREAS_BY_USE_CASE[useCase].includes(a)),
    gradeBand: GRADE_BAND_USE_CASES.includes(useCase) ? answers.gradeBand : null,
  };
}
